import {
  ElementRef,
  Injectable,
  NgZone,
  OnDestroy,
  Renderer2,
  inject,
  signal,
} from '@angular/core';
import { Store } from '@ngxs/store';
import {
  MAX_UNASSIGNED_REGION_HEIGHT_RATIO,
  MIN_UNASSIGNED_REGION_HEIGHT_PX,
  UNASSIGNED_REGION_LEFT_PANEL_MIN_HEIGHT_PX,
} from '@wilson/interfaces';
import {
  ShiftTimelineDataState,
  ShiftTimelineUnassignedRegionState,
} from '@wilson/shift-timeline/state';
import { Subject, Subscription, auditTime } from 'rxjs';

type HeightPxActionCtor = new (heightPx: number) => object;

interface ExtraSizedElement {
  element: HTMLElement;
  property: 'height' | 'max-height';
}

// Provide this per-component (in that component's own `providers` array),
// never `providedIn: 'root'` - each instance is tied via ElementRef to
// whichever component's host element provided it. Regular view and cell
// view commit to separate store fields (their natural heights are on
// completely different scales), so each consumer must call setHeightAction
// with its own action before any drag can happen - see ngOnInit in each.
@Injectable()
export class UnassignedRegionResizeService implements OnDestroy {
  private readonly store = inject(Store);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly ngZone = inject(NgZone);

  private dragStartHeightPx = 0;
  private totalAvailableHeightPx = 0;
  private readonly isDraggingSignal = signal(false);
  readonly isDragging = this.isDraggingSignal.asReadonly();
  private readonly isLeftPanelContentVisibleSignal = signal(false);
  readonly isLeftPanelContentVisible =
    this.isLeftPanelContentVisibleSignal.asReadonly();
  private heightActionCtor: HeightPxActionCtor | null = null;
  private toggleExpandedCallback: ((isExpanded: boolean) => void) | null = null;
  private currentIsExpanded: boolean | null = null;
  private readonly resizeCommitSubject = new Subject<number>();
  private extraSizedElements: ExtraSizedElement[] = [];

  private readonly subscription: Subscription = this.resizeCommitSubject
    .pipe(auditTime(50))
    .subscribe((heightPx) => {
      this.ngZone.run(() => this.dispatchHeightPx(heightPx));
    });

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  setHeightAction(actionCtor: HeightPxActionCtor): void {
    this.heightActionCtor = actionCtor;
  }

  setExtraSizedElements(elements: ExtraSizedElement[]): void {
    this.extraSizedElements = elements;
  }

  setToggleExpandedCallback(callback: (isExpanded: boolean) => void): void {
    this.toggleExpandedCallback = callback;
  }

  onResizeStart(): void {
    this.dragStartHeightPx =
      this.elementRef.nativeElement.getBoundingClientRect().height;
    this.totalAvailableHeightPx =
      this.dragStartHeightPx +
      this.store.selectSnapshot(ShiftTimelineDataState.currentTimelineHeightPx);
    this.currentIsExpanded = this.store.selectSnapshot(
      ShiftTimelineUnassignedRegionState.isUnassignedRegionExpanded,
    );
    this.ngZone.run(() => this.isDraggingSignal.set(true));
  }

  onResize(deltaY: number): void {
    const heightPx = this.clampHeightPx(this.dragStartHeightPx + deltaY);
    this.applyHeightPx(heightPx);
    this.resizeCommitSubject.next(heightPx);
    this.possiblyToggleExpanded(heightPx);
  }

  onResizeEnd(deltaY: number): void {
    const heightPx = this.clampHeightPx(this.dragStartHeightPx + deltaY);
    this.applyHeightPx(heightPx);
    this.ngZone.run(() => {
      this.dispatchHeightPx(heightPx);
      this.isDraggingSignal.set(false);
    });
    this.possiblyToggleExpanded(heightPx);
  }

  private dispatchHeightPx(heightPx: number): void {
    if (!this.heightActionCtor || heightPx <= MIN_UNASSIGNED_REGION_HEIGHT_PX) {
      return;
    }
    this.store.dispatch(new this.heightActionCtor(heightPx));
  }

  private possiblyToggleExpanded(heightPx: number): void {
    const isExpanded = heightPx > MIN_UNASSIGNED_REGION_HEIGHT_PX;
    if (isExpanded === this.currentIsExpanded) {
      return;
    }
    this.currentIsExpanded = isExpanded;
    const callback = this.toggleExpandedCallback;
    if (!callback) {
      return;
    }
    this.ngZone.run(() => callback(isExpanded));
  }

  private possiblyToggleLeftPanelContentVisible(heightPx: number): void {
    const isVisible = heightPx >= UNASSIGNED_REGION_LEFT_PANEL_MIN_HEIGHT_PX;
    if (isVisible === this.isLeftPanelContentVisibleSignal()) {
      return;
    }
    this.ngZone.run(() => this.isLeftPanelContentVisibleSignal.set(isVisible));
  }

  applyHeightPx(heightPx: number): void {
    this.renderer.setStyle(
      this.elementRef.nativeElement,
      'height',
      `${heightPx}px`,
    );
    for (const { element, property } of this.extraSizedElements) {
      this.renderer.setStyle(element, property, `${heightPx}px`);
    }
    this.possiblyToggleLeftPanelContentVisible(heightPx);
  }

  private clampHeightPx(heightPx: number): number {
    const maxHeightPx =
      this.totalAvailableHeightPx * MAX_UNASSIGNED_REGION_HEIGHT_RATIO;
    return Math.min(
      Math.max(heightPx, MIN_UNASSIGNED_REGION_HEIGHT_PX),
      maxHeightPx,
    );
  }
}
